// Carry Roll App shell — Header + SidebarNav + overlays.
//
// calc-sidebar's SidebarNav owns top-level navigation (7 screens exceed
// calc-nav's 5-item tab cap). All screen panels stay mounted so component
// state survives navigation (UX Invariant #8). The sidebar is responsive:
// a sticky column with a collapse chevron on desktop, a slide-in drawer on
// mobile.

import { useEffect } from 'react';
import { AlertProvider } from '@ascend/alert';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import { SettingsDrawer } from 'calc-nav';
import { SidebarNav } from 'calc-sidebar';
import appIdentity from 'virtual:app-identity';
import { Header } from './components/shell/Header';
import { LoadDemoModal } from './components/shell/LoadDemoModal';
import { PersistErrorBanner } from './components/shell/PersistErrorBanner';
import { QuickSettings } from './components/shell/QuickSettings';
import { NAV_ITEMS } from './components/shell/navItems';
import { FeedbackForm, FeedbackButton } from './components/FeedbackForm/FeedbackForm';
import { NAV_SECTION } from './lib/nav';
import { cryIDBAdapter } from './persist/idb';
import { useStore } from './store';
import { useRecompute } from './store/selectors';
import { useUI } from './store/uiStore';
import { useTheme } from './lib/useTheme';

const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

export default function App() {
  // Wire memoized recompute on every render path.
  useRecompute();

  // Sync the documentElement `dark` class to the persisted/system theme.
  useTheme();

  // One-time hydration: seed sessions/profiles/bag from IndexedDB on mount.
  // AppGate only renders <App/> once unlocked, so the idb adapter (if any) is
  // wired by the time this runs. Empty/locked IDB → keep in-memory defaults.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sessions, profiles, bag] = await Promise.all([
          cryIDBAdapter.getAllSessions(),
          cryIDBAdapter.getAllProfiles(),
          cryIDBAdapter.getAllBag(),
        ]);
        if (cancelled) return;
        useStore.setState((s) => ({
          sessions: sessions.length ? sessions : s.sessions,
          profiles: profiles.length ? profiles : s.profiles,
          bag: bag.length ? bag : s.bag,
        }));
      } catch {
        /* IDB unavailable (e.g. locked/private mode) — keep in-memory defaults. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const aboutOpen = useUI((s) => s.aboutOpen);
  const closeAbout = useUI((s) => s.closeAbout);
  const settingsDrawerOpen = useUI((s) => s.settingsDrawerOpen);
  const closeSettingsDrawer = useUI((s) => s.closeSettingsDrawer);
  const feedbackOpen = useUI((s) => s.feedbackOpen);
  const feedbackConfig = useUI((s) => s.feedbackConfig);
  const openFeedback = useUI((s) => s.openFeedback);
  const closeFeedback = useUI((s) => s.closeFeedback);

  return (
    <AlertProvider>
      <div
        className="flex min-h-screen flex-col"
        style={{ backgroundColor: 'var(--color-bg)' }}
        data-testid="app-root"
      >
        <Header />
        <PersistErrorBanner />
        {/* SidebarNav renders its own flex row (sidebar + content). The host
            container establishes the flex context and lets children shrink. */}
        <div className="flex min-h-0 flex-1" data-testid="app-body">
          <SidebarNav
            section={NAV_SECTION}
            items={NAV_ITEMS}
            ariaLabel="Primary"
            sidebarWidth="200px"
          />
        </div>
        <AboutModal isOpen={aboutOpen} onClose={closeAbout} config={aboutConfig} />
        <SettingsDrawer
          isOpen={settingsDrawerOpen}
          onClose={closeSettingsDrawer}
          title="Quick Settings"
        >
          <QuickSettings />
        </SettingsDrawer>
        <FeedbackForm
          isOpen={feedbackOpen}
          onClose={closeFeedback}
          initialType={feedbackConfig.type}
          initialCategory={feedbackConfig.category}
          initialError={feedbackConfig.error}
        />
        <FeedbackButton onOpen={openFeedback} />
        <LoadDemoModal />
      </div>
    </AlertProvider>
  );
}
